/** Normalize v2 checkin-type list row (summary card) for table display. */
export function normalizeCheckinTypeListRow(raw) {
  const ct = raw?.checkin_type ?? raw ?? {};
  const entitlement = raw?.entitlement ?? {};
  const pool = entitlement.limit_pool_category ?? ct.limit_pool_category ?? {};
  const statusVal = ct.checkin_type_status ?? ct.checkin_types_status ?? ct.status?.value;

  return {
    checkin_type_id: ct.checkin_type_id ?? raw?.checkin_type_id ?? raw?.id,
    checkin_type_name: ct.checkin_type_name ?? raw?.checkin_type_name,
    checkin_types_description:
      ct.checkin_type_description ?? ct.checkin_types_description ?? raw?.checkin_types_description,
    checkin_types_status: statusVal,
    status_label: ct.status?.label,
    display_order: ct.display_order ?? raw?.display_order,
    active_from: ct.active_from ?? raw?.active_from,
    active_to: ct.active_to ?? raw?.active_to,
    entitlement_mode: entitlement.entitlement_mode ?? ct.entitlement_mode ?? raw?.entitlement_mode ?? 'generic',
    entitlement_mode_label: entitlement.entitlement_mode_label,
    plans_categories_id:
      entitlement.plans_categories_id ?? ct.plans_categories_id ?? raw?.plans_categories_id,
    limit_pool_category_name: pool.plans_categories_name,
    facility_type_mode: raw?.facility_type_mode ?? ct.modes?.facility_type_mode?.value,
    facility_mode: raw?.facility_mode ?? ct.modes?.facility_mode?.value,
    facility_mode_label: ct.modes?.facility_mode?.label,
    category_mode: raw?.category_mode,
    tariff_mode: raw?.tariff_mode,
    item_mode: raw?.item_mode,
    policy_summary: raw?.policy_summary ?? {},
    linked_plan_count: entitlement.linked_plan_count ?? raw?.linked_plan_count,
  };
}

/** Params for tariff-scoped category/item lookups (draft or saved type). */
export function buildTariffLookupParams(tariffLinks = [], checkinTypeId) {
  const params = {};
  if (checkinTypeId && checkinTypeId !== 'new') {
    params.checkin_type_id = checkinTypeId;
  }
  const links = (tariffLinks || [])
    .map(({ key, standard_tariff_label, facility_tariff_label, link_facility_id, link_facility_label, ...rest }) => rest)
    .filter((l) => l.standard_tariff_id && (l.tariff_kind ?? 'standard') === 'standard');
  if (links.length === 1) {
    params.standard_tariff_id = links[0].standard_tariff_id;
    if (links[0].category_id != null) params.category_id = links[0].category_id;
  } else if (links.length > 1) {
    params.tariff_links = JSON.stringify(links);
  }
  return params;
}

/** Derive encounter-type category policy from price-list rows (single source of truth). */
export function categoryPolicyFromTariffLinks(tariffLinks = [], tariffMode = 'open') {
  if (tariffMode === 'open') {
    return { categoryMode: 'open', categoryIds: [] };
  }
  const links = (tariffLinks || []).filter(
    (l) => l.standard_tariff_id && (l.tariff_kind ?? 'standard') === 'standard',
  );
  if (!links.length) {
    return { categoryMode: 'open', categoryIds: [] };
  }
  const hasUnscopedLink = links.some((l) => l.category_id == null || l.category_id === '');
  if (hasUnscopedLink) {
    return { categoryMode: 'open', categoryIds: [] };
  }
  const categoryIds = [...new Set(
    links.map((l) => Number(l.category_id)).filter((id) => Number.isFinite(id) && id > 0),
  )];
  return {
    categoryMode: categoryIds.length ? 'include' : 'open',
    categoryIds,
  };
}

/** Human-readable price-list → service group lines for the Services summary tab. */
export function tariffServiceGroupSummary(tariffLinks = [], categoryOptions = []) {
  const labelById = new Map(
    (categoryOptions || []).map((o) => [Number(o.value), o.label]),
  );
  return (tariffLinks || [])
    .filter((l) => l.standard_tariff_id && (l.tariff_kind ?? 'standard') === 'standard')
    .map((l) => {
      const tariff = l.standard_tariff_label || `Standard tariff #${l.standard_tariff_id}`;
      const catId = l.category_id;
      const cat = catId != null && catId !== ''
        ? (labelById.get(Number(catId)) || `Category #${catId}`)
        : 'All service groups on this tariff';
      return `${tariff} → ${cat}`;
    });
}

export function categoryOptionsFromLookupList(list = []) {
  return list.map((c) => ({
    value: c.id ?? c.category_id,
    label: c.category_name ?? c.name ?? c.label ?? `Category ${c.id ?? c.category_id}`,
  })).filter((o) => o.value != null);
}

export const itemRuleValue = (row) =>
  row?.standardized_item_id ?? row?.item_id ?? row?.tariff_item_id ?? row?.facility_tariff_item_id ?? undefined;

export const idsToFacilityOptions = (ids = []) =>
  ids.map((id) => ({ value: id, label: `Facility #${id}` }));

/** Build facility select options from hydrated detail `facilities` or id list. */
export function facilityTypeOptionsFromLookupList(list = []) {
  return list.map((t) => ({
    value: t.facility_type_id ?? t.id ?? t.value,
    label: t.facility_type_name ?? t.name ?? t.label ?? `Type ${t.facility_type_id ?? t.id}`,
  })).filter((o) => o.value != null);
}

/** Build facility-type select options from hydrated detail `facility_types` or id list. */
export function facilityTypeOptionsFromBundle(bundle) {
  const hydrated = Array.isArray(bundle?.facility_types) ? bundle.facility_types : [];
  if (hydrated.length) {
    return facilityTypeOptionsFromLookupList(hydrated);
  }
  return (bundle?.facility_type_ids ?? []).map((id) => ({
    value: id,
    label: `Facility type #${id}`,
  }));
}

export function facilityOptionsFromBundle(bundle) {
  const hydrated = Array.isArray(bundle?.facilities) ? bundle.facilities : [];
  if (hydrated.length) {
    return hydrated.map((f) => {
      const id = f.facility_id ?? f.id;
      const name = f.facility_name ?? f.name;
      const place = [f.facility_city, f.facility_state].filter(Boolean).join(', ');
      const label = name
        ? (place ? `${name} — ${place}` : name)
        : `Facility #${id}`;
      return { value: id, label };
    }).filter((o) => o.value != null);
  }
  return idsToFacilityOptions(bundle?.facility_ids ?? []);
}

/** Map API tariff link (with nested standard_tariff / facility_tariff) to form row. */
export function tariffLinkFromApi(link, index) {
  const std = link?.standard_tariff;
  const fac = link?.facility_tariff;
  return {
    ...link,
    key: link.key ?? `tl-${index}`,
    standard_tariff_label:
      std?.tariff_name ?? link.standard_tariff_label
      ?? (link.standard_tariff_id ? `Standard tariff #${link.standard_tariff_id}` : null),
    facility_tariff_label:
      fac?.tariff_name ?? link.facility_tariff_label
      ?? (link.facility_tariff_id ? `Facility tariff #${link.facility_tariff_id}` : null),
    link_facility_id: link.link_facility_id ?? fac?.facility_id ?? null,
    link_facility_label: link.link_facility_label ?? null,
  };
}

/** Map API item rule (with costable_item / label) to form row. */
export function itemRuleFromApi(rule, index) {
  const ci = rule?.costable_item;
  let label = rule.label;
  if (!label && ci?.item_name) {
    label = ci.item_code ? `${ci.item_name} (${ci.item_code})` : ci.item_name;
  }
  if (!label && itemRuleValue(rule)) {
    label = `Item #${itemRuleValue(rule)}`;
  }
  return {
    ...rule,
    key: rule.key ?? `ir-${index}`,
    label,
    evidence_mode: rule.evidence_mode ?? 'not_needed',
  };
}

/** Merge hydrated category objects into select options. */
export function categoryOptionsFromHydrated(categories = []) {
  return categories.map((c) => ({
    value: c.id ?? c.category_id,
    label: c.category_name ?? c.name ?? `Category ${c.id ?? c.category_id}`,
  })).filter((o) => o.value != null);
}

export const EVIDENCE_MODES = [
  { value: 'not_needed', label: 'Not needed — no upload' },
  { value: 'optional', label: 'Optional — may attach' },
  { value: 'required', label: 'Required — blocks submit' },
];

export const CLAIMS_INITIAL_STATUS_OPTIONS = [
  { value: 0, label: 'Pending CO (Claims Officer)' },
  { value: 1, label: 'Pending HM (Head of Medical)' },
  { value: 2, label: 'Pending CFO' },
];

/** Extract workflow settings from detail bundle. */
export function workflowFromBundle(bundle) {
  const wf = bundle?.workflow ?? {};
  return {
    pa_header_evidence_mode: wf.pa_header_evidence_mode ?? 'not_needed',
    pa_auto_approve: Boolean(wf.pa_auto_approve),
    encounter_auto_close: Boolean(wf.encounter_auto_close),
    claims_initial_status: wf.claims_initial_status ?? 0,
  };
}

/** Map API category rule (with nested category) to form row. */
export function categoryEvidenceRuleFromApi(rule, index) {
  const cat = rule?.category ?? {};
  const categoryId = rule.category_id ?? cat.id ?? cat.category_id;
  return {
    key: rule.key ?? `cer-${index}`,
    category_id: categoryId,
    category_name: cat.category_name ?? cat.name ?? rule.category_name ?? null,
    evidence_mode: rule.evidence_mode ?? 'not_needed',
  };
}

/** Extract entitlement fields from detail bundle. */
export function entitlementFromBundle(bundle) {
  const ct = bundle?.checkin_type ?? {};
  const ent = bundle?.entitlement ?? {};
  const pool = ent.limit_pool_category ?? ct.limit_pool_category ?? {};
  return {
    entitlement_mode: ent.entitlement_mode ?? ct.entitlement_mode ?? 'generic',
    plans_categories_id:
      ent.plans_categories_id ?? ct.plans_categories_id ?? ct.limit_pool_category_id ?? pool.plans_categories_id ?? null,
    limit_pool_category: pool,
  };
}
